import { useCallback, useRef } from 'react';

const useOverlayPlayers = ({
    loadHeroImage,
    loadItemsForPlayer,
    setActivePlayers,
    setOverlayVisible,
    setGameActiveState,
}) => {
    const globalPlayersRef = useRef({});

    const clearAllPortraits = useCallback(() => {
        setActivePlayers({});
        setOverlayVisible(false);
        setGameActiveState(false);
        globalPlayersRef.current = {};
    }, [setActivePlayers, setOverlayVisible, setGameActiveState]);

    const handleGameEnd = useCallback((data) => {
        clearAllPortraits();
    }, [clearAllPortraits]);

    const handlePlayerCreate = useCallback((data) => {
        if (!data || typeof data !== 'object') return;

        const playerSlotsCount = Object.keys(globalPlayersRef.current).length;
        const player_slot = data.player_slot || data.slot_position || data.entity_index || playerSlotsCount + 1;

        if (globalPlayersRef.current[player_slot]) return;

        setGameActiveState(true);

        let playerTeam;
        if (data.team === 2 || data.team === 'amber') {
            playerTeam = 'amber';
        } else if (data.team === 3 || data.team === 'sapphire') {
            playerTeam = 'sapphire';
        } else {
            playerTeam = 'amber';
        }

        const playerData = {
            ...data,
            team: playerTeam,
            assigned_lane: data.assigned_lane ?? data.original_assigned_lane ?? 0,
            original_assigned_lane: data.original_assigned_lane ?? data.assigned_lane ?? 0,
            slot_position: data.slot_position || data.player_slot
        };

        globalPlayersRef.current[player_slot] = playerData;
        setActivePlayers(prev => ({
            ...prev,
            [player_slot]: playerData
        }));

        if (data.hero_id && data.hero_id !== 'N/A') {
            loadHeroImage(data.hero_id).then(heroInfo => {
                if (!heroInfo) return;
                const finalPlayer = {
                    ...globalPlayersRef.current[player_slot],
                    hero_name: heroInfo.name,
                    hero_image: heroInfo.imageUrl
                };
                globalPlayersRef.current[player_slot] = finalPlayer;
                setActivePlayers(prev => ({
                    ...prev,
                    [player_slot]: finalPlayer
                }));
            });
        }

        if (data.upgrades && Array.isArray(data.upgrades) && data.upgrades.length > 0) {
            loadItemsForPlayer(data.upgrades).then(items => {
                if (!items || items.length === 0) return;
                const finalPlayer = {
                    ...globalPlayersRef.current[player_slot],
                    items
                };
                globalPlayersRef.current[player_slot] = finalPlayer;
                setActivePlayers(prev => ({
                    ...prev,
                    [player_slot]: finalPlayer
                }));
            });
        }
    }, [loadHeroImage, loadItemsForPlayer, setActivePlayers, setGameActiveState]);

    const removePlayer = useCallback((player_slot) => {
        if (!player_slot) return;
        if (!globalPlayersRef.current[player_slot]) return;
        delete globalPlayersRef.current[player_slot];
        setActivePlayers(prev => {
            const newState = { ...prev };
            delete newState[player_slot];
            return newState;
        });
    }, [setActivePlayers]);

    const handlePlayerUpdate = useCallback((data) => {
        const player_slot = data?.player_slot || data?.slot_position || data?.entity_index;

        if (globalPlayersRef.current[player_slot]) {
            const currentPlayer = globalPlayersRef.current[player_slot];
            const preservedHeroData = {
                hero_image: currentPlayer.hero_image,
                hero_name: currentPlayer.hero_name,
                hero_id: currentPlayer.hero_id
            };

            let updatedPlayer = { ...currentPlayer };
            Object.keys(data).forEach((key) => {
                if (data[key] !== null && data[key] !== undefined) {
                    updatedPlayer[key] = data[key];
                }
            });

            updatedPlayer.hero_image = data.hero_image !== undefined ? data.hero_image : preservedHeroData.hero_image;
            updatedPlayer.hero_name = data.hero_name !== undefined ? data.hero_name : preservedHeroData.hero_name;
            updatedPlayer.hero_id = data.hero_id !== undefined ? data.hero_id : preservedHeroData.hero_id;

            if (data.hero_id && data.hero_id !== 'N/A' && data.hero_id !== currentPlayer.hero_id) {
                loadHeroImage(data.hero_id).then(heroInfo => {
                    if (!heroInfo) return;
                    const finalPlayer = {
                        ...globalPlayersRef.current[player_slot],
                        hero_name: heroInfo.name,
                        hero_image: heroInfo.imageUrl,
                        hero_id: data.hero_id
                    };
                    globalPlayersRef.current[player_slot] = finalPlayer;
                    setActivePlayers(prev => ({
                        ...prev,
                        [player_slot]: finalPlayer
                    }));
                });
            }

            if (updatedPlayer.hero_id && updatedPlayer.hero_id !== 'N/A' && !updatedPlayer.hero_image) {
                loadHeroImage(updatedPlayer.hero_id).then(heroInfo => {
                    if (!heroInfo) return;
                    const finalPlayer = {
                        ...globalPlayersRef.current[player_slot],
                        hero_name: heroInfo.name,
                        hero_image: heroInfo.imageUrl
                    };
                    globalPlayersRef.current[player_slot] = finalPlayer;
                    setActivePlayers(prev => ({
                        ...prev,
                        [player_slot]: finalPlayer
                    }));
                });
            }

            globalPlayersRef.current[player_slot] = updatedPlayer;
            setActivePlayers(prev => ({
                ...prev,
                [player_slot]: updatedPlayer
            }));

            if (data.upgrades && Array.isArray(data.upgrades)) {
                const currentUpgradesStr = JSON.stringify(currentPlayer.upgrades || []);
                const newUpgradesStr = JSON.stringify(data.upgrades);

                if (currentUpgradesStr !== newUpgradesStr) {
                    loadItemsForPlayer(data.upgrades).then(items => {
                        if (!items || items.length === 0) return;
                        const finalPlayer = {
                            ...globalPlayersRef.current[player_slot],
                            items
                        };
                        globalPlayersRef.current[player_slot] = finalPlayer;
                        setActivePlayers(prev => ({
                            ...prev,
                            [player_slot]: finalPlayer
                        }));
                    });
                }
            }

            return;
        }

        if (!data || typeof data !== 'object') return;

        const playerSlotsCount = Object.keys(globalPlayersRef.current).length;
        const safeData = {
            ...data,
            player_slot: data.player_slot || data.slot_position || data.entity_index || playerSlotsCount + 1,
            assigned_lane: data.assigned_lane || 0,
            original_assigned_lane: data.original_assigned_lane || 0,
            slot_position: data.slot_position || data.player_slot || data.entity_index || playerSlotsCount + 1
        };

        handlePlayerCreate(safeData);
    }, [handlePlayerCreate, loadHeroImage, loadItemsForPlayer, setActivePlayers]);

    return {
        globalPlayersRef,
        clearAllPortraits,
        handleGameEnd,
        handlePlayerCreate,
        handlePlayerUpdate,
        removePlayer,
    };
};

export default useOverlayPlayers;

